import axios, { AxiosResponse } from 'axios';
import { ApiResponse, PaginatedResponse, Product, Order, Category, UserBalance } from '../types';

// API 기본 설정
/**
 * 🔴 2026-08-30: 폴백이 `http://localhost:3001` 이었다. 이 값은 **사용자 브라우저**에서
 *    자기 컴퓨터를 가리키므로 절대 연결되지 않는다(ERR_CONNECTION_REFUSED).
 *    Dockerfile 이 .env 없이 빌드해 폴백이 그대로 번들에 박혔고,
 *    KAHAN Mall 홈의 추천/최근 상품이 **하나도 뜨지 않았다.**
 * 🔑 폴백을 상대경로로 둔다. nginx 가 /api 를 백엔드로 프록시하므로
 *    도메인이 무엇이든(mall.kahan.club·dev·로컬) 그대로 동작한다 —
 *    빌드 시점 환경변수에 기대지 않는 것이 이 자리의 근본 해결이다.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터 - 인증 토큰 추가
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터 - 에러 처리
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 인증 만료 시 로그아웃 처리
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API 서비스 클래스
export class ApiService {
  
  // 상품 관련 API
  static async getProducts(params?: {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Product>> {
    const response: AxiosResponse<PaginatedResponse<Product>> = await api.get('/products', { params });
    return response.data;
  }

  static async getProduct(id: number): Promise<ApiResponse<Product>> {
    const response: AxiosResponse<ApiResponse<Product>> = await api.get(`/products/${id}`);
    return response.data;
  }

  static async getProductsByCategory(category: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Product>> {
    const response: AxiosResponse<PaginatedResponse<Product>> = await api.get(`/products/category/${category}`, { params });
    return response.data;
  }

  // 카테고리 관련 API
  static async getCategories(): Promise<ApiResponse<Category[]>> {
    const response: AxiosResponse<ApiResponse<Category[]>> = await api.get('/categories');
    return response.data;
  }

  // 장바구니 관련 API
  static async addToCart(productId: number, quantity: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.post('/cart/add', {
      productId,
      quantity
    });
    return response.data;
  }

  static async getCart(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.get('/cart');
    return response.data;
  }

  static async updateCartItem(productId: number, quantity: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.put(`/cart/items/${productId}`, {
      quantity
    });
    return response.data;
  }

  static async removeFromCart(productId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.delete(`/cart/items/${productId}`);
    return response.data;
  }

  static async clearCart(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.delete('/cart');
    return response.data;
  }

  // 주문 관련 API
  static async createOrder(orderData: {
    items: Array<{ productId: number; quantity: number; }>;
    usePoints: {
      cashPoints: number;
      goldPoints: number;
    };
    shippingAddress?: any;
  }): Promise<ApiResponse<Order>> {
    const response: AxiosResponse<ApiResponse<Order>> = await api.post('/orders', orderData);
    return response.data;
  }

  static async getOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedResponse<Order>> {
    const response: AxiosResponse<PaginatedResponse<Order>> = await api.get('/orders', { params });
    return response.data;
  }

  static async getOrder(id: number): Promise<ApiResponse<Order>> {
    const response: AxiosResponse<ApiResponse<Order>> = await api.get(`/orders/${id}`);
    return response.data;
  }

  static async cancelOrder(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.put(`/orders/${id}/cancel`);
    return response.data;
  }

  // 사용자 관련 API
  static async getUserBalance(): Promise<ApiResponse<UserBalance>> {
    const response: AxiosResponse<ApiResponse<UserBalance>> = await api.get('/user/balance');
    return response.data;
  }

  // 인증 관련 API (기존 KAHAN 시스템과 통합)
  static async login(credentials: {
    username: string;
    password: string;
  }): Promise<ApiResponse<{ token: string; user: any }>> {
    const response: AxiosResponse<ApiResponse<{ token: string; user: any }>> = await api.post('/auth/login', credentials);
    return response.data;
  }

  static async getUserProfile(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.get('/auth/profile');
    return response.data;
  }

  static async logout(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.post('/auth/logout');
    return response.data;
  }

  static async register(userData: {
    username: string;
    email: string;
    password: string;
    name: string;
    secondName?: string;
    phoneNumber: string;
    sponsorId: string;
    position: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.post('/register-mlm', userData);
    return response.data;
  }

  static async validateSponsor(sponsorId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.get(`/validate-sponsor/${sponsorId}`);
    return response.data;
  }

  // 임시 토큰 시스템 (안전한 확장)
  static async verifyTempToken(token: string): Promise<ApiResponse<{
    user: {
      id: number;
      username: string;
      name: string;
      email: string;
    };
    purpose: string;
  }>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.get(`/auth/verify-temp-token/${token}`);
    return response.data;
  }

  // 검색 관련 API
  static async searchProducts(query: string, params?: {
    category?: string;
    priceRange?: { min: number; max: number; };
    paymentType?: string;
  }): Promise<PaginatedResponse<Product>> {
    const response: AxiosResponse<PaginatedResponse<Product>> = await api.get('/search', {
      params: { q: query, ...params }
    });
    return response.data;
  }

  // 추천 상품 API
  static async getFeaturedProducts(limit?: number): Promise<ApiResponse<Product[]>> {
    const response: AxiosResponse<ApiResponse<Product[]>> = await api.get('/products/featured', {
      params: { limit }
    });
    return response.data;
  }

  static async getRecentProducts(limit?: number): Promise<ApiResponse<Product[]>> {
    const response: AxiosResponse<ApiResponse<Product[]>> = await api.get('/products/recent', {
      params: { limit }
    });
    return response.data;
  }

  // 결제 관련 API
  static async calculateDiscount(userId: number, amount: number): Promise<ApiResponse<{
    originalAmount: number;
    discountRate: number;
    discountAmount: number;
    finalAmount: number;
  }>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.post('/payments/calculate-discount', {
      userId,
      amount
    });
    return response.data;
  }

  static async validatePointBalance(points: {
    cashPoints: number;
    goldPoints: number;
  }): Promise<ApiResponse<{
    isValid: boolean;
    availableCashPoints: number;
    availableGoldPoints: number;
  }>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.post('/payments/validate-balance', points);
    return response.data;
  }
}

export default api;